import { useState } from "react";
import { Panel } from "@/components/ui/Panel";
import {
  type Definitions,
  type TitleMatch,
  titleMatches,
} from "@/lib/element-registry";
import { useEscapeScope } from "@/components/ui/escape-scope";
import { wrappedIndex } from "@/lib/wrapped-index";

/**
 * A drawing as the tags need to name it.
 *
 * Held here beside the label rule rather than in the card, so the offers and the sharing
 * tags name a figure the same way rather than each formatting it for themselves.
 */
export interface NamedFigure {
  file_id: string;
  label?: string | null;
}

/** The offer list's own id, and one per offer, so the field can point at what it holds. */
export function offersId(elementId: string): string {
  return `element-title-offers-${elementId}`;
}

function offerId(listId: string, elementId: string): string {
  return `${listId}-${elementId}`;
}

/** How a drawing is named, or the bare word when the set cannot name it. */
export function figureLabel(figures: NamedFigure[], fileId: string): string {
  return `FIG. ${figures.find((f) => f.file_id === fileId)?.label ?? ""}`;
}

/**
 * The offer list under a title field: which hits are showing, and whether the list is open.
 *
 * Open and highlight belong with the list. The lookup is a title read; the list is what
 * paints digits from the pane's map.
 */
export function useTitleOfferList(definitions: Definitions, typed: string) {
  const [dismissed, setDismissed] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const matches = dismissed ? [] : titleMatches(definitions, typed);
  const active = Math.min(highlighted, matches.length - 1);

  const close = () => {
    setDismissed(true);
    setHighlighted(-1);
  };

  // The offers are a popover, so they take Escape as the nearest scope and one press does
  // not also abandon whatever the list happens to be open on top of.
  useEscapeScope("popover", matches.length > 0, close);

  return {
    matches,
    highlighted: active,
    close,
    setHighlighted,
    onTyped: () => {
      setDismissed(false);
      setHighlighted(-1);
    },
  };
}

/**
 * The elements the set already holds, offered under the field as it is typed.
 *
 * Numbering is shared across the set, so an attorney naming a part on one drawing cannot see
 * that another drawing already holds it. Each offer therefore shows the digits the pane
 * already projected, and the drawings that pin it: what a pick adopts is what it shows.
 *
 * Picked on mousedown rather than on click, because blur fires first on a real pointer press
 * and the list closes with it, so a click handler on an item that has already gone would
 * never run.
 */
export function TitleOffers({
  matches,
  highlighted,
  assigned,
  figures,
  listId,
  onPick,
}: {
  matches: TitleMatch[];
  highlighted: number;
  assigned: Map<string, string>;
  figures: NamedFigure[];
  listId: string;
  onPick: (match: TitleMatch) => void;
}) {
  if (matches.length === 0) return null;
  return (
    <Panel
      as="ul"
      shape="row"
      pad="none"
      id={listId}
      role="listbox"
      aria-label="Elements this set already holds"
      className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden shadow-md"
    >
      {matches.map((match, index) => (
        <li
          key={match.elementId}
          id={offerId(listId, match.elementId)}
          role="option"
          aria-selected={index === highlighted}
          onMouseDown={(event) => {
            // The press must not take the field's focus, or blur commits the draft and the
            // row can unmount before the pick is issued.
            event.preventDefault();
            onPick(match);
          }}
          className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[12px] ${index === highlighted ? "bg-muted" : ""}`}
        >
          <span className="font-mono text-[11px] font-semibold">
            {assigned.get(match.elementId)}
          </span>
          <span className="flex-1 truncate">{match.title}</span>
          <span className="whitespace-nowrap text-[10px] text-muted-foreground">
            {match.fileIds.map((id) => figureLabel(figures, id)).join(" ")}
          </span>
        </li>
      ))}
    </Panel>
  );
}

/**
 * What a key does to the offers under the field.
 *
 * Escape dismisses them and the arrows move the highlight. Enter takes whatever is
 * highlighted, and saves the typed words when nothing is: those are two different things,
 * and only one of them is the attorney's own. Taking the first offer for them would hand
 * their element away on a keystroke they did not aim, and holding on to what they typed
 * until they happen to leave the field loses the save they just asked for.
 *
 * Enter is answered before the offers are consulted, so it means the same thing whether or
 * not a list happens to be open.
 */
export function keyOnOffers(
  event: React.KeyboardEvent,
  {
    matches,
    highlighted,
    setHighlighted,
    pick,
    submit,
  }: {
    matches: TitleMatch[];
    highlighted: number;
    setHighlighted: (index: number) => void;
    pick: (match: TitleMatch) => void;
    submit: () => void;
  },
): void {
  if (event.key === "Enter") {
    event.preventDefault();
    return highlighted >= 0 ? pick(matches[highlighted]) : submit();
  }
  if (matches.length === 0) return;
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    setHighlighted(
      wrappedIndex(
        highlighted,
        event.key === "ArrowDown" ? 1 : -1,
        matches.length,
      ),
    );
  }
}

/**
 * What the field says about the offers under it, in the editable-combobox pattern.
 *
 * Without these the offers appear in silence: a reader is told neither that a list opened
 * nor which of its rows the arrow keys are on.
 */
export function comboboxProps(
  elementId: string,
  matches: TitleMatch[],
  highlighted: number,
) {
  return {
    role: "combobox",
    "aria-expanded": matches.length > 0,
    "aria-controls": offersId(elementId),
    "aria-autocomplete": "list" as const,
    "aria-activedescendant":
      highlighted >= 0
        ? offerId(offersId(elementId), matches[highlighted].elementId)
        : undefined,
  };
}
